import { ref, onUnmounted } from 'vue'
import AppConfig from '@/config'

type Timer = ReturnType<typeof setTimeout>
type Interval = ReturnType<typeof setInterval>

interface UseAudioRecordingOptions {
	// Вызывается, когда запись остановлена и в ней была речь (не тишина).
	onAudioReady: (blob: Blob) => void | Promise<void>
}

const SILENCE_THRESHOLD = 115
const ANALYSER_FFT_SIZE = 256
const SILENCE_CHECK_INTERVAL = 1000

export function useAudioRecording(options: UseAudioRecordingOptions) {
	const isRecording = ref(false)
	const isForbidden = ref(false)

	const mediaRecorder = ref<MediaRecorder | null>(null)
	const audioChunks = ref<Blob[]>([])
	const audioUrl = ref<string | null>(null)
	const audioContext = ref<AudioContext | null>(null)
	const analyzer = ref<AnalyserNode | null>(null)

	const silenceTimer = ref<Timer | null>(null)
	const maxDurationTimer = ref<Timer | null>(null)
	const silenceInterval = ref<Interval | null>(null)
	const isSilent = ref(false)

	async function start(): Promise<void> {
		try {
			isRecording.value = true
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
			mediaRecorder.value = new MediaRecorder(stream, { mimeType: 'audio/webm; codecs=opus' })
			setupAudioAnalysis(stream)
			setupTimers()
			setupEvents()
			mediaRecorder.value.start()
		} catch (err) {
			if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')) {
				isForbidden.value = true
			}
			isRecording.value = false
			cleanup()
		}
	}

	function stop(): void {
		if (isRecording.value && mediaRecorder.value) {
			mediaRecorder.value.stop()
			clearSilenceTimer()
			clearMaxDurationTimer()
			clearSilenceInterval()
			isRecording.value = false
			mediaRecorder.value.stream.getTracks().forEach((track) => track.stop())
		}
	}

	function setupAudioAnalysis(stream: MediaStream): void {
		audioContext.value = new AudioContext()
		const microphone = audioContext.value.createMediaStreamSource(stream)
		analyzer.value = audioContext.value.createAnalyser()
		analyzer.value.fftSize = ANALYSER_FFT_SIZE
		microphone.connect(analyzer.value)
	}

	function setupTimers(): void {
		maxDurationTimer.value = setTimeout(() => {
			if (mediaRecorder.value?.state === 'recording') stop()
		}, AppConfig.maxAudioRequestDuration)

		isSilent.value = true
		silenceInterval.value = setInterval(checkSilence, SILENCE_CHECK_INTERVAL)
	}

	function setupEvents(): void {
		if (!mediaRecorder.value) return
		mediaRecorder.value.ondataavailable = (event) => {
			audioChunks.value.push(event.data)
		}
		mediaRecorder.value.onstop = () => {
			void handleRecordingStop()
		}
	}

	function checkSilence(): void {
		if (!analyzer.value) return
		const dataArray = new Uint8Array(analyzer.value.frequencyBinCount)
		analyzer.value.getByteFrequencyData(dataArray)
		isSilent.value = dataArray.every((value) => value <= SILENCE_THRESHOLD)

		if (isSilent.value && !silenceTimer.value) {
			silenceTimer.value = setTimeout(() => {
				stop()
			}, AppConfig.maxAudioSilenceDuration)
		} else if (!isSilent.value) {
			clearSilenceTimer()
			clearSilenceInterval()
		}
	}

	async function handleRecordingStop(): Promise<void> {
		clearSilenceTimer()
		clearSilenceInterval()
		if (!isSilent.value) {
			const audioBlob = new Blob(audioChunks.value, { type: 'audio/webm' })
			audioUrl.value = URL.createObjectURL(audioBlob)
			try {
				await options.onAudioReady(audioBlob)
			} finally {
				audioChunks.value = []
				isRecording.value = false
			}
		}
		cleanup()
	}

	function clearSilenceTimer(): void {
		if (silenceTimer.value) {
			clearTimeout(silenceTimer.value)
			silenceTimer.value = null
		}
	}

	function clearSilenceInterval(): void {
		if (silenceInterval.value) {
			clearInterval(silenceInterval.value)
			silenceInterval.value = null
		}
	}

	function clearMaxDurationTimer(): void {
		if (maxDurationTimer.value) {
			clearTimeout(maxDurationTimer.value)
			maxDurationTimer.value = null
		}
	}

	function cleanup(): void {
		if (audioContext.value) {
			audioContext.value.close()
			audioContext.value = null
		}
		if (audioUrl.value) {
			URL.revokeObjectURL(audioUrl.value)
			audioUrl.value = null
		}
		analyzer.value = null
		mediaRecorder.value = null
	}

	onUnmounted(() => {
		if (isRecording.value) stop()
		cleanup()
	})
	return {
		isRecording,
		isForbidden,
		start,
		stop
	}
}
